import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  Badge,
  Card,
  Collapse,
  ToastContainer,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { CSVLink } from "react-csv";
import ReactToPrint from "react-to-print";
import {
  ArrowBarLeft,
  ArrowBarRight,
  ArrowClockwise,
  FiletypeCsv,
  FilterCircle,
  PenFill,
  PrinterFill,
  ThreeDots,
} from "react-bootstrap-icons";

// custom hooks import
import useISPowner from "../../../hooks/useISPOwner";

// internal import
import Sidebar from "../../../components/admin/sidebar/Sidebar";
import useDash from "../../../assets/css/dash.module.css";
import { FontColor, FourGround } from "../../../assets/js/theme";
import Loader from "../../../components/common/Loader";
import Table from "../../../components/table/Table";
import Footer from "../../../components/admin/footer/Footer";
import {
  fetchReseller,
  getAllPackages,
  getArea,
  resellerCustomerReport,
} from "../../../features/apiCalls";
import ReportView from "../../report/modal/ReportView";
import EditReport from "../../report/modal/EditReport";
import FormatNumber from "../../../components/common/NumberFormat";
import PrintReport from "../../report/ReportPDF";
import { adminResellerCommission } from "./CommissionShear";
import { badge } from "../../../components/common/Utils";
import DataFilter from "../../common/DataFilter";
import useDataState from "../../../hooks/useDataState";
import {
  getPoleBoxApi,
  getSubAreasApi,
} from "../../../features/actions/customerApiCall";
import useSelectorState from "../../../hooks/useSelectorState";

const ResellerCollection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const componentRef = useRef();

  var today = new Date();
  var firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  firstDay.setHours(0, 0, 0, 0);
  today.setHours(23, 59, 59, 999);

  const filterInputPermission = {
    month: true,
    mikrotik: true,
    package: true,
    area: true,
    subArea: true,
    poleBox: true,
    reseller: true,
    billType: true,
    billingType: true,
    medium: true,
    startCreateDate: true,
    endCreateDate: true,
    userType: true,
  };

  //---> Get filtered dataset from useDataState hook
  const { filterOptions, setFilterOption } = useDataState();

  //---> Get redux store state data from useSelectorState hooks
  const { areas, subAreas, polesBox, allPackages } = useSelectorState();

  // get user & current user data form useISPOwner
  const { ispOwnerData, ispOwnerId, userData } = useISPowner();

  // get all reseller data from redux store
  const reseller = useSelector((state) => state?.reseller?.reseller);

  //get reseller collection report data
  const collectionReport = useSelector(
    (state) => state?.reseller?.resellerCollection
  );

  //loading state
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoader, setDataLoader] = useState(false);
  const [packageLoading, setPackageLoading] = useState(false);

  // filter Accordion handle state
  const [activeKeys, setActiveKeys] = useState("");

  // reseller customer collection main data state
  const [currentData, setCurrentData] = useState([]);

  // report id state
  const [viewId, setViewId] = useState("");
  const [reportId, setReportId] = useState("");

  // note state
  const [note, setNote] = useState("");

  // table card handle
  const [open, setOpen] = useState(false);

  // filter date state
  const [filterDate, setFilterDate] = useState(firstDay);

  useEffect(() => {
    //---> @Get ispOwner areas data
    !areas?.length && getArea(dispatch, ispOwnerId, setLoading);

    //---> @Get ispOwner areas sub-area data
    !subAreas.length && getSubAreasApi(dispatch, ispOwnerId);

    // get reseller api
    reseller.length === 0 && fetchReseller(dispatch, ispOwnerId, setDataLoader);

    //---> @Get ispOwner sub-areas pol-box data
    !polesBox?.length && getPoleBoxApi(dispatch, ispOwnerId, setLoading);

    // get ispOwner all pakcages api
    allPackages.length === 0 &&
      getAllPackages(dispatch, ispOwnerId, setPackageLoading);
  }, []);

  useEffect(() => {
    if (filterOptions?.month !== undefined) {
      var createStart = new Date(
        filterOptions?.month?.getFullYear(),
        filterOptions?.month?.getMonth(),
        1
      );
      var createEnd = new Date(
        filterOptions?.month?.getFullYear(),
        filterOptions?.month?.getMonth() + 1,
        0
      );
      createEnd.setHours(23, 59, 59, 999);

      if (createEnd?.getMonth() + 1 === today.getMonth() + 1) {
        setFilterOption({
          ...filterOptions,
          startCreateDate: createStart,
          endCreateDate: today,
        });
      } else {
        setFilterOption({
          ...filterOptions,
          startCreateDate: createStart,
          endCreateDate: createEnd,
        });
      }

      resellerCustomerReport(
        dispatch,
        ispOwnerId,
        filterOptions?.month?.getFullYear(),
        filterOptions?.month?.getMonth() + 1,
        setIsLoading
      );
    }
  }, [filterOptions?.month]);

  useEffect(() => {
    setCurrentData(collectionReport);
  }, [collectionReport]);

  //reload handler
  const reloadHandler = () => {
    resellerCustomerReport(
      dispatch,
      ispOwnerId,
      filterOptions?.month?.getFullYear(),
      filterOptions?.month?.getMonth() + 1,
      setIsLoading
    );
  };

  // set Report function
  const getReportId = (id) => {
    setReportId(id);
    setNote("");
  };

  const columns = useMemo(
    () => [
      {
        width: "6%",
        Header: t("id"),
        accessor: (field) =>
          field?.hotspotCustomer
            ? field?.hotspotCustomer?.customerId
            : field?.customer?.customerId,
        Cell: ({ row: { original } }) => (
          <div>
            <p className="text-center">
              {original?.hotspotCustomer
                ? original?.hotspotCustomer?.customerId
                : original?.customer?.customerId}
            </p>
            <Badge
              bg={
                original.customer?.userType === "pppoe"
                  ? "primary"
                  : original.customer?.userType === "static"
                  ? "info"
                  : "secondary"
              }
            >
              {original.customer?.userType === "pppoe"
                ? "PPPoE"
                : original.customer?.userType === "static"
                ? "Static"
                : original.customer?.userType === "hotspot"
                ? "Hotspot"
                : ""}
            </Badge>
          </div>
        ),
      },
      {
        width: "15%",
        Header: t("pppoeIp"),
        accessor: (data) =>
          `${data.customer?.name} ${data.customer?.pppoe?.name} ${data.customer?.queue?.target} ${data.hotspotCustomer?.hotspot?.name}`,
        Cell: ({ row: { original } }) => (
          <div className="text-start">
            <p>{original.customer?.name}</p>
            <p>
              {original.customer?.userType === "pppoe"
                ? original.customer?.pppoe.name
                : original.customer?.userType === "static"
                ? original.customer?.queue.target
                : original.hotspotCustomer?.hotspot.name}
            </p>
          </div>
        ),
      },
      {
        width: "10%",
        Header: t("packageBill"),
        accessor: "amount",
        Cell: ({ row: { original } }) => {
          return (
            <div>
              <p>
                {original.customer?.mikrotikPackage?.name
                  ? original.customer?.mikrotikPackage?.name
                  : original.customer?.userType === "pppoe"
                  ? original.customer?.pppoe?.profile
                  : original?.hotspotCustomer?.hotspot.profile}
              </p>
              <p style={{ fontWeight: "500" }}>৳{original?.amount}</p>
            </div>
          );
        },
      },
      {
        width: "8%",
        Header: t("due"),
        accessor: "due",
      },
      {
        width: "10%",
        Header: t("TypeMedium"),
        accessor: (field) => `${field?.billingType} ${field?.medium}`,
        Cell: ({ row: { original } }) => {
          return (
            <div>
              <p style={{ fontWeight: "500" }}>{original?.medium}</p>
              <p>{badge(original?.billingType)}</p>
            </div>
          );
        },
      },
      {
        width: "8%",
        Header: t("collected"),
        accessor: "name",
      },
      {
        width: "10%",
        Header: t("otherCommission"),
        Cell: ({ row: { original } }) => (
          <div className="text-start">
            <p>
              {t("own")} &nbsp;
              <span style={{ fontWeight: "500" }}>
                ৳
                {original?.ispOwnerCommission
                  ? original.ispOwnerCommission
                  : adminResellerCommission(reseller, original)
                      ?.ispOwnerCommission}
              </span>
            </p>

            <p>
              {t("reseller")} &nbsp;
              <span style={{ fontWeight: "500" }}>
                ৳
                {original?.resellerCommission
                  ? original.resellerCommission
                  : adminResellerCommission(reseller, original)
                      ?.resellerCommission}
              </span>
            </p>
          </div>
        ),
      },
      {
        width: "6%",
        Header: t("note"),
        accessor: "month",
      },
      {
        width: "7%",
        Header: t("date"),
        accessor: "createdAt",
        Cell: ({ cell: { value } }) => {
          return moment(value).format("YYYY/MM/DD hh:mm A");
        },
      },
    ],
    [t, allPackages]
  );

  // set csv header
  const resellerCollectionCsVTableInfoHeader = [
    { label: "Name", key: "name" },
    { label: "Package", key: "package" },
    { label: "Bill_Amount", key: "amount" },
    { label: "Bill_Discount", key: "discount" },
    { label: "Bill_Medium", key: "medium" },
    { label: "Collector_Name", key: "collector" },
    { label: "Comment", key: "comment" },
    { label: "Bill_Collect_Date", key: "createdAt" },
  ];

  // get data from current data for csv
  let resellerCollectionCsVTableInfo = currentData.map((data) => {
    const note = data?.note ? data?.note : "";
    let start = data?.start ? moment(data?.start).format("YYYY/MM/DD") : "";
    let end = data?.end ? moment(data?.end).format("YYYY/MM/DD") : "";
    return {
      name: data?.customer?.name,
      package: data.package,
      amount: data.amount,
      discount: data.discount,
      medium: data.medium,
      collector: data.name,
      comment: note + " - " + start + " - " + end,
      createdAt: moment(data.createdAt).format("YYYY/MM/DD"),
    };
  });

  // find select reseller
  const resellerName = reseller.find(
    (item) => item.id === filterOptions?.reseller
  );

  // print filter table data heading
  const filterData = {
    reseller: resellerName?.name ? resellerName.name : t("all"),
    medium: filterOptions?.medium ? filterOptions?.medium : t("all"),
    startDate: filterOptions?.startCreateDate,
    endDate: filterOptions?.endCreateDate,
    totalBill: currentData.reduce((prev, current) => prev + current.amount, 0),
  };

  //function to calculate total Commissions and other amount
  const totalSum = () => {
    const initialValue = {
      amount: 0,
      resellerCommission: 0,
      ispOwnerCommission: 0,
    };

    const calculatedValue = currentData?.reduce((acc, item) => {
      acc.amount += item.amount || 0;

      const commissionData = adminResellerCommission(reseller, item) || {};

      acc.resellerCommission +=
        item.resellerCommission ?? commissionData.resellerCommission ?? 0;

      acc.ispOwnerCommission +=
        item.ispOwnerCommission ?? commissionData.ispOwnerCommission ?? 0;

      return acc;
    }, initialValue);

    return calculatedValue || initialValue;
  };

  const customComponent = (
    <div
      className="text-center"
      style={{ fontSize: "18px", fontWeight: "500", display: "flex" }}
    >
      {totalSum()?.amount > 0 && (
        <div>
          {t("collection")} :
          <span className="fw-bold"> ৳{FormatNumber(totalSum().amount)}</span>
        </div>
      )}
      <div className="mx-3">
        {t("admin")} :
        <span className="fw-bold">
          &nbsp; ৳{FormatNumber(totalSum().ispOwnerCommission)}
        </span>
      </div>
      <div>
        {t("reseller")} :
        <span className="fw-bold">
          &nbsp;৳{FormatNumber(totalSum().resellerCommission)}
        </span>
      </div>
    </div>
  );

  return (
    <>
      <Sidebar />
      <ToastContainer position="top-right" theme="colored" />
      <div className={useDash.dashboardWrapper}>
        <div className="container-fluied collector">
          <div className="container">
            <FontColor>
              <FourGround>
                <div className="collectorTitle d-flex justify-content-between px-4">
                  <h2>{t("resellerCollection")}</h2>

                  <div
                    style={{ height: "45px" }}
                    className="d-flex align-items-center"
                  >
                    <div
                      onClick={() => {
                        if (!activeKeys) {
                          setActiveKeys("filter");
                        } else {
                          setActiveKeys("");
                        }
                      }}
                      title={t("filter")}
                    >
                      <FilterCircle className="addcutmButton" />
                    </div>

                    <div className="reloadBtn">
                      {isLoading ? (
                        <Loader />
                      ) : (
                        <ArrowClockwise
                          className="arrowClock"
                          title={t("refresh")}
                          onClick={reloadHandler}
                        />
                      )}
                    </div>

                    <Collapse in={open} dimension="width">
                      <div id="example-collapse-text">
                        <Card className="cardCollapse border-0">
                          <div className="d-flex align-items-center">
                            <div className="addAndSettingIcon">
                              <CSVLink
                                data={resellerCollectionCsVTableInfo}
                                filename={ispOwnerData.company}
                                headers={resellerCollectionCsVTableInfoHeader}
                                title={t("resellerCollection")}
                              >
                                <FiletypeCsv className="addcutmButton" />
                              </CSVLink>
                            </div>

                            <div className="addAndSettingIcon">
                              <ReactToPrint
                                documentTitle={t("billReport")}
                                trigger={() => (
                                  <PrinterFill
                                    title={t("print")}
                                    className="addcutmButton"
                                  />
                                )}
                                content={() => componentRef.current}
                              />
                            </div>
                          </div>
                        </Card>
                      </div>
                    </Collapse>

                    {!open && (
                      <ArrowBarLeft
                        className="ms-1"
                        size={34}
                        style={{ cursor: "pointer" }}
                        onClick={() => setOpen(!open)}
                        aria-controls="example-collapse-text"
                        aria-expanded={open}
                      />
                    )}

                    {open && (
                      <ArrowBarRight
                        className="ms-1"
                        size={34}
                        style={{ cursor: "pointer" }}
                        onClick={() => setOpen(!open)}
                        aria-controls="example-collapse-text"
                        aria-expanded={open}
                      />
                    )}
                  </div>
                </div>
              </FourGround>

              <FourGround>
                <div className="mt-2">
                  <Accordion alwaysOpen activeKey={activeKeys}>
                    <Accordion.Item eventKey="filter">
                      <Accordion.Body>
                        <DataFilter
                          filterData={collectionReport}
                          setFilterData={setCurrentData}
                          filterOptions={filterOptions}
                          setFilterOption={setFilterOption}
                          inputPermission={filterInputPermission}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                  <div className="collectorWrapper pb-2">
                    <div style={{ display: "none" }}>
                      <PrintReport
                        filterData={filterData}
                        currentCustomers={currentData}
                        ref={componentRef}
                        status="collection"
                      />
                    </div>

                    <div className="table-section">
                      <Table
                        isLoading={isLoading}
                        customComponent={customComponent}
                        columns={columns}
                        data={currentData}
                      ></Table>
                    </div>
                  </div>
                </div>
              </FourGround>
              <Footer />
            </FontColor>
          </div>
        </div>
      </div>
      <EditReport
        reportId={reportId}
        note={note}
        setNote={setNote}
        status="resellerCustomerReport"
      />
      <ReportView reportId={viewId} status="resellerCustomerReport" />
    </>
  );
};

export default ResellerCollection;
